import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, Lock, CheckCircle, ArrowLeft } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_luxury-credit-eval/artifacts/8w7azhpp_a8299614-dead-4387-811d-09deff6f81fe.png";

// Ecwid Product Widget Component
const EcwidProductWidget = () => {
  useEffect(() => {
    // Load Ecwid script
    const script = document.createElement('script');
    script.src = 'https://app.ecwid.com/script.js?43569238&data_platform=singleproduct_v2';
    script.async = true;
    script.charset = 'utf-8';
    script.setAttribute('data-cfasync', 'false');
    
    script.onload = () => {
      if (window.xProduct) {
        window.xProduct();
      }
    };
    
    document.body.appendChild(script);
    
    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src*="app.ecwid.com"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div 
      className="ecsp ecsp-SingleProduct-v2 ecsp-SingleProduct-v2-bordered ecsp-SingleProduct-v2-centered ecsp-Product ec-Product-272618446" 
      itemScope 
      itemType="http://schema.org/Product" 
      data-single-product-id="272618446"
    >
      <div itemProp="image"></div>
      <div className="ecsp-title" itemProp="name" content="Evaluation"></div>
      <div itemType="http://schema.org/Offer" itemScope itemProp="offers">
        <div className="ecsp-productBrowser-price ecsp-price" itemProp="price" content="15" data-spw-price-location="button">
          <div itemProp="priceCurrency" content="USD"></div>
        </div>
      </div>
      <div customprop="options"></div>
      <div customprop="qty"></div>
      <div customprop="addtobag"></div>
      <div customprop="vatinprice"></div>
    </div>
  );
};

const EvaluationPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/5 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="eval-logo">
            <img src={LOGO_URL} alt="Line of Luxury" className="h-12" />
          </Link>
          <Link 
            to="/" 
            className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors flex items-center gap-2 text-sm"
            data-testid="eval-back-link"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 px-6">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Title */}
            <div className="text-center mb-10">
              <h1 className="font-serif text-3xl md:text-4xl mb-4" data-testid="eval-title">
                Credit <span className="gold-gradient-text">Evaluation</span>
              </h1>
              <p className="text-[#A1A1AA] font-sans">
                Start your credit evaluation journey today
              </p>
            </div>

            {/* Security Notice */}
            <div className="bg-[#161616] border border-[#D4AF37]/20 p-4 mb-8 flex items-start gap-3" data-testid="security-notice">
              <Lock size={20} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <p className="text-[#A1A1AA] text-sm font-sans leading-relaxed">
                Your information is encrypted and securely used only to analyze your 
                credit report for evaluation purposes. We never share your data with third parties.
              </p>
            </div>

            {/* Ecwid Checkout Widget */}
            <div className="bg-[#161616] border border-white/5 p-8" data-testid="ecwid-checkout">
              <div className="ecwid-widget-container">
                <EcwidProductWidget />
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 mt-8">
              <div className="trust-badge">
                <Shield size={18} />
                <span>Secure Checkout</span>
              </div>
              <div className="trust-badge">
                <Lock size={18} />
                <span>Encrypted Data</span>
              </div>
              <div className="trust-badge">
                <CheckCircle size={18} />
                <span>Trusted Service</span>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-[#52525B] text-sm font-sans">
            © {new Date().getFullYear()} Line of Luxury LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default EvaluationPage;
